import { Injectable, SecurityContext } from '@angular/core';
import { AppSettings, CachedData, ClientApplicationConfiguration } from '@shared/generalInterfaces';
import { DomSanitizer } from '@angular/platform-browser';

@Injectable({
	providedIn: 'root',
})
export class ClientDiscoveryService {
	private clientApplications = new CachedData<ClientApplicationConfiguration[]>([], 0);
	public expiration = 60 * 60 * 1000;
	private uri = 'component-config/all';

	constructor(private readonly sanitizer: DomSanitizer) {
	}

	async getClientApplication(appId: string): Promise<ClientApplicationConfiguration> {
		const appsConfig = this.clientApplications.data.filter(x => x.appId === appId);

		if (this.clientApplications.expiresOn > new Date().getTime() && appsConfig.length === 1) {
			return appsConfig[0];
		}

		await this.initApplications();

		return this.clientApplications.data.filter(x => x.appId === appId)[0];
	}

	async getClientApplications(appIds: string[]): Promise<ClientApplicationConfiguration[]> {
		const uniqueAppIds = this.removeDuplications(appIds);
		const appsConfig = this.clientApplications.data.filter(x => uniqueAppIds.includes(x.appId));

		if (this.clientApplications.expiresOn > new Date().getTime() || appsConfig.length === uniqueAppIds.length) {
			return appsConfig;
		}

		await this.initApplications();

		return this.clientApplications.data.filter(x => uniqueAppIds.includes(x.appId));
	}

	reset() {
		this.clientApplications = new CachedData<ClientApplicationConfiguration[]>([], 0);
	}

	private async initApplications() {
		try {
			const config = await this.loadAppConfig();
			const clientDiscoveryUrl = config.routingData.clientDiscoveryUrl;
			const response = await fetch(`${clientDiscoveryUrl}${this.uri}`);
			const apps = (await response.json()) as ClientApplicationConfiguration[];

			if (apps && apps.length > 0) {
				this.sanitizeUrl(apps);
				this.clientApplications = new CachedData<ClientApplicationConfiguration[]>(apps, this.expiration);
			} else {
				throw new Error('Client applications response is empty');
			}
		} catch (err) {
			throw err;
		}
	}

	private async loadAppConfig(): Promise<AppSettings> {
		const response = await fetch('/assets/config/appSettings.json');

		return (await response.json()) as AppSettings;
	}

	private removeDuplications(arr: any[]): any[] {
		return [...new Set(arr)];
	}

	private sanitizeUrl(apps: ClientApplicationConfiguration[]) {
		return apps.map(app => {
			const sanitizedUrl = this.sanitizer.sanitize(SecurityContext.URL, app.url);

			return { ...app, url: sanitizedUrl };
		});
	}
}
